export function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error ?? '');
}

export function isHttpStatusError(error: unknown, status: number): boolean {
  const message = getErrorMessage(error);
  return message.includes(`HTTP ${status}`) || message.includes(`(${status})`);
}

export function isUnauthorizedHttpError(error: unknown): boolean {
  const message = getErrorMessage(error);
  return isHttpStatusError(error, 401)
    || message.includes('유효하지 않은 JWT 토큰입니다.')
    || message.includes('세션이 만료되었거나 폐기되었습니다.')
    || message.includes('유효하지 않은 사용자 세션입니다.')
    || message.includes('토큰에 세션 식별자(jti)가 없습니다.')
    || message.includes('Authorization Bearer 토큰이 필요합니다.');
}

export function isForbiddenHttpError(error: unknown): boolean {
  return isHttpStatusError(error, 403);
}

export function isNotFoundHttpError(error: unknown): boolean {
  return isHttpStatusError(error, 404);
}

export function isWorkspaceItemAccessDeniedError(error: unknown): boolean {
  return isForbiddenHttpError(error);
}

export function isWorkspaceItemUnavailableError(error: unknown): boolean {
  return isForbiddenHttpError(error) || isNotFoundHttpError(error);
}

/* ------------------------------------------------------------------ */
/* Agent capacity limits (429)                                         */
/* ------------------------------------------------------------------ */

/** Why the server refused to start the turn. */
export type CapacityLimitReason =
  /** Another turn is already running against this document/session. */
  | 'resource_busy'
  /** This user already has the maximum number of turns running. */
  | 'user_limit'
  /** The server as a whole is at its concurrent-turn ceiling. */
  | 'total_limit';

export interface CapacityLimitInfo {
  reason: CapacityLimitReason | string;
  /** The ceiling that was hit. */
  limit: number;
  /** How many were running when the request was refused. */
  active: number;
  retryAfterSec: number;
}

/**
 * Extract the structured refusal from a 429 thrown by `streamAgent`.
 *
 * `streamAgent` surfaces a non-OK response as
 * `Error("HTTP 429: <raw body>")`, so the payload has to be recovered
 * from the message. Backend shape (FastAPI wraps `detail`):
 *
 *   {"detail": {"error": {"code": "capacity_limit", "reason": ..., ...}}}
 *
 * Returns `null` for anything that isn't a capacity refusal, including
 * a 429 from some other source — callers must not show capacity copy
 * for an unrelated rate limit.
 */
export function parseCapacityLimitError(error: unknown): CapacityLimitInfo | null {
  if (!isHttpStatusError(error, 429)) return null;

  const message = getErrorMessage(error);
  const start = message.indexOf('{');
  if (start === -1) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(message.slice(start));
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;

  const envelope = parsed as Record<string, unknown>;
  const detail = (envelope.detail ?? envelope) as Record<string, unknown>;
  const payload = detail?.error as Record<string, unknown> | undefined;
  if (!payload || payload.code !== 'capacity_limit') return null;

  const reason = typeof payload.reason === 'string' ? payload.reason : '';
  if (!reason) return null;

  const toInt = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

  return {
    reason,
    limit: toInt(payload.limit, 0),
    active: toInt(payload.active, 0),
    retryAfterSec: toInt(payload.retry_after_sec, 0),
  };
}

export function isCapacityLimitError(error: unknown): boolean {
  return parseCapacityLimitError(error) !== null;
}
